import { useParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { serviceBySlug, services } from "../data/services";
import { cities } from "../data/cities";
import { provinces } from "../data/provinces";
import { getHead } from "../seo/routes";
import { serviceIntro, serviceFaqs } from "../seo/content";
import { Seo } from "../components/Seo";
import { PageHero, Prose, FAQBlock, CTABand, Breadcrumbs, LinkGrid, SectionHead } from "../components/Blocks";
import NotFound from "./NotFound";

export default function ServicePage() {
  const { slug } = useParams();
  const service = serviceBySlug[slug];
  if (!service) return <NotFound />;
  const path = `/services/${service.slug}`;
  const faqs = serviceFaqs(service);
  const head = getHead(path, { service, faqs });
  const related = services.filter((s) => s.slug !== service.slug).slice(0, 8);
  return (
    <>
      <Seo title={head.title} description={head.description} path={path} />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: service.name }]} />
      <PageHero overline="Security Service" title={service.name} lead={service.short} image={service.image} />
      <Prose paragraphs={[serviceIntro(service), `${service.angle} Every engagement begins with a site walk-through and written post orders, continues with supervised shifts and GPS-verified checkpoints, and ends with time-stamped reporting you can audit at any time.`]} />
      <section className="bg-[#131620] border-y border-white/10" data-testid="service-deliverables">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <SectionHead overline="Scope of Work" title={`What Our ${service.name} Includes`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.deliverables.map((d) => (
              <div key={d} className="flex items-start gap-3 border border-white/10 bg-[#0D0E12] p-4 rounded-sm" data-testid={`deliverable-${d.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
                <CheckCircle2 className="h-5 w-5 text-[#FF5500] shrink-0 mt-0.5" />
                <span className="text-sm text-gray-300 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <LinkGrid
        overline="Coverage"
        title={`${service.name} Available in ${cities.length} Canadian Cities`}
        items={cities.map((c) => ({ to: `/${service.slug}-in-${c.slug}`, label: c.name, testid: `service-city-${service.slug}-${c.slug}` }))}
      />
      <LinkGrid
        overline="By Province"
        title={`${service.name} by Province & Territory`}
        cols="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        items={provinces.map((p) => ({ to: `/${service.slug}-in-${p.slug}`, label: p.name, testid: `service-province-${p.slug}` }))}
      />
      <FAQBlock faqs={faqs} heading={`${service.name} — Common Questions`} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="related-services">
        <SectionHead overline="Explore" title="Related Security Services" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {related.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-service-${s.slug}`} className="group flex items-center justify-between border border-white/10 bg-[#181C28] hover:border-[#FF5500]/50 rounded-sm px-4 py-3 transition-all duration-200">
              <span className="text-sm text-gray-300 group-hover:text-white">{s.name}</span>
              <ArrowRight className="h-3.5 w-3.5 text-[#FF5500]" />
            </Link>
          ))}
        </div>
      </section>
      <CTABand title={`Deploy ${service.name} This Week`} sub="Call our 24/7 dispatch or email the coordination team. A site walk-through and written post orders come before the first shift — always." />
    </>
  );
}
